<template>
  <Teleport to="body">
    <transition name="archived-drawer">
      <div
        v-if="open"
        class="archived-drawer-backdrop"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @click.self="emit('close')"
      >
        <aside class="archived-drawer">
          <header class="archived-drawer-header">
            <div class="archived-drawer-header-text">
              <div class="archived-drawer-icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
              <div>
                <h2 class="archived-drawer-title">{{ title }}</h2>
                <p class="archived-drawer-count" v-if="!loading && total > 0">
                  {{ total }} {{ total === 1 ? t('archivedDrawer.item') : t('archivedDrawer.items') }}
                </p>
              </div>
            </div>
            <button type="button" class="archived-drawer-close" :aria-label="t('archivedDrawer.close')" @click="emit('close')">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </header>

          <p class="archived-drawer-hint">
            <svg class="archived-drawer-hint-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {{ hintText }}
          </p>

          <div class="archived-drawer-body">
            <div v-if="loading" class="archived-drawer-loading">
              <div class="archived-drawer-spinner" />
              <span>{{ t('archivedDrawer.loading') }}</span>
            </div>

            <div v-else-if="!total" class="archived-drawer-empty">
              <div class="archived-drawer-empty-icon" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-2.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <p>{{ emptyTextResolved }}</p>
            </div>

            <ul v-else class="archived-drawer-list">
              <li v-for="item in items" :key="itemKey(item)" class="archived-drawer-card">
                <div class="archived-drawer-card-fields">
                  <div
                    v-for="field in fields"
                    :key="field.key"
                    class="archived-drawer-field"
                    :class="{ 'archived-drawer-field--primary': field.primary }"
                  >
                    <span class="archived-drawer-field-label">{{ field.label }}</span>
                    <MoneyAmount
                      v-if="field.format === 'money'"
                      :value="item?.[field.key]"
                      size="sm"
                      class="archived-drawer-field-value"
                    />
                    <span v-else class="archived-drawer-field-value">{{ formatField(item, field) }}</span>
                  </div>
                </div>
                <button
                  type="button"
                  class="archived-drawer-action"
                  :disabled="busyId === itemKey(item)"
                  @click="emit('desarquivar', item)"
                >
                  <svg v-if="busyId !== itemKey(item)" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  {{ busyId === itemKey(item) ? t('archivedDrawer.pleaseWait') : actionLabelResolved }}
                </button>
              </li>
            </ul>
          </div>

          <footer v-if="showPagination" class="archived-drawer-footer">
            <p class="archived-drawer-range">{{ rangeLabel }}</p>
            <div class="archived-drawer-pagination">
              <button
                type="button"
                class="archived-drawer-page-btn"
                :disabled="loading || page <= 0"
                :aria-label="t('archivedDrawer.prevPage')"
                @click="emit('page-change', page - 1)"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span class="archived-drawer-page-indicator">{{ page + 1 }} / {{ totalPages }}</span>
              <button
                type="button"
                class="archived-drawer-page-btn"
                :disabled="loading || page >= totalPages - 1"
                :aria-label="t('archivedDrawer.nextPage')"
                @click="emit('page-change', page + 1)"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </footer>
        </aside>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import MoneyAmount from './MoneyAmount.vue'

const { t } = useI18n()

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, required: true },
  hint: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
  page: { type: Number, default: 0 },
  pageSize: { type: Number, default: 30 },
  totalPages: { type: Number, default: 0 },
  fields: {
    type: Array,
    required: true
    /** { key, label, primary?, format?: 'money' } */
  },
  emptyText: { type: String, default: '' },
  busyId: { type: [Number, String], default: null },
  itemIdKey: { type: String, default: 'id' },
  actionLabel: { type: String, default: '' },
})

const emit = defineEmits(['close', 'desarquivar', 'page-change'])

const hintText = computed(() => props.hint || t('archivedDrawer.defaultHint'))
const emptyTextResolved = computed(() => props.emptyText || t('archivedDrawer.defaultEmpty'))
const actionLabelResolved = computed(() => props.actionLabel || t('archivedDrawer.unarchive'))

const showPagination = computed(() => props.totalPages > 1)

const rangeLabel = computed(() => {
  if (!props.total) return ''
  const from = props.page * props.pageSize + 1
  const to = Math.min((props.page + 1) * props.pageSize, props.total)
  return t('archivedDrawer.range', { from, to, total: props.total })
})

const itemKey = (item) => item?.[props.itemIdKey]

const formatField = (item, field) => {
  const raw = item?.[field.key]
  if (raw === null || raw === undefined || raw === '') return t('archivedDrawer.emptyValue')
  return String(raw)
}
</script>

<style scoped>
.archived-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
}

.archived-drawer {
  display: flex;
  flex-direction: column;
  width: min(440px, 100vw);
  height: 100%;
  background: var(--bg-secondary);
  border-left: 1px solid var(--border-primary);
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.18);
}

.archived-drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
  background: linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
}

.archived-drawer-header-text {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.archived-drawer-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: #fff;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.35);
}

.archived-drawer-icon svg {
  width: 24px;
  height: 24px;
}

.archived-drawer-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-primary);
  line-height: 1.3;
}

.archived-drawer-count {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
}

.archived-drawer-close {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.archived-drawer-close:hover {
  background: var(--bg-hover, var(--bg-primary));
  color: var(--text-primary);
  border-color: var(--border-hover, var(--border-primary));
}

.archived-drawer-close svg {
  width: 20px;
  height: 20px;
}

.archived-drawer-hint {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 16px 20px 0;
  padding: 12px 14px;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--text-secondary);
  background: rgba(99, 102, 241, 0.08);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
}

.archived-drawer-hint-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  color: #6366f1;
}

.archived-drawer-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px 12px;
}

.archived-drawer-footer {
  flex-shrink: 0;
  padding: 12px 20px 20px;
  border-top: 1px solid var(--border-primary);
  background: var(--bg-secondary);
}

.archived-drawer-range {
  margin: 0 0 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary);
  text-align: center;
}

.archived-drawer-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.archived-drawer-page-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  background: var(--bg-primary);
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.archived-drawer-page-btn:hover:not(:disabled) {
  border-color: rgba(99, 102, 241, 0.45);
  background: rgba(99, 102, 241, 0.08);
}

.archived-drawer-page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.archived-drawer-page-btn svg {
  width: 20px;
  height: 20px;
}

.archived-drawer-page-indicator {
  min-width: 72px;
  text-align: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.archived-drawer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 48px 16px;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.archived-drawer-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--border-primary);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: archived-spin 0.7s linear infinite;
}

@keyframes archived-spin {
  to {
    transform: rotate(360deg);
  }
}

.archived-drawer-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.archived-drawer-empty-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: var(--bg-primary);
  border: 1px dashed var(--border-primary);
  color: var(--text-secondary);
  opacity: 0.7;
}

.archived-drawer-empty-icon svg {
  width: 28px;
  height: 28px;
}

.archived-drawer-empty p {
  margin: 0;
  max-width: 240px;
  line-height: 1.5;
}

.archived-drawer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.archived-drawer-card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.archived-drawer-card:hover {
  border-color: rgba(99, 102, 241, 0.35);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.archived-drawer-card-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.archived-drawer-field--primary {
  grid-column: 1 / -1;
}

.archived-drawer-field-label {
  display: block;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.archived-drawer-field-value {
  display: block;
  font-size: 0.9375rem;
  font-weight: 500;
  color: var(--text-primary);
  line-height: 1.35;
  word-break: break-word;
}

.archived-drawer-field--primary .archived-drawer-field-value {
  font-size: 1.0625rem;
  font-weight: 600;
}

.archived-drawer-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3);
}

.archived-drawer-action:hover:not(:disabled) {
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.4);
}

.archived-drawer-action:active:not(:disabled) {
  transform: scale(0.98);
}

.archived-drawer-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.archived-drawer-action svg {
  width: 18px;
  height: 18px;
}

.archived-drawer-enter-active,
.archived-drawer-leave-active {
  transition: opacity 0.22s ease;
}

.archived-drawer-enter-active .archived-drawer,
.archived-drawer-leave-active .archived-drawer {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}

.archived-drawer-enter-from,
.archived-drawer-leave-to {
  opacity: 0;
}

.archived-drawer-enter-from .archived-drawer,
.archived-drawer-leave-to .archived-drawer {
  transform: translateX(100%);
}
</style>
